import jsPDF from 'jspdf';

import brandLogoUrl from '../assets/BrandHorizontalRed.png';
import type { InvoiceHistoryItem } from '../types/vida/table/invoiceHistoryTableType';
import { formatSubestipulanteCodNome } from './subestipulanteUtil';

export type InvoicePdfLabels = {
  invoiceMonth: string;
  contractNumber: string;
  subestipulanteName: string;
  type: string;
  dueDate: string;
  status: string;
  amountSection: string;
};

const DEFAULT_INVOICE_PDF_LABELS: InvoicePdfLabels = {
  invoiceMonth: 'MÊS DA FATURA',
  contractNumber: 'NÚMERO DA APÓLICE',
  subestipulanteName: 'SUB-ESTIPULANTE',
  type: 'TIPO',
  dueDate: 'DATA DE VENCIMENTO',
  status: 'STATUS',
  amountSection: 'Valor da Fatura',
};

/** Labels alinhados com InvoiceHistoryTable (aba Pagamentos / Vida). */
export const VIDA_INVOICE_PDF_LABELS: InvoicePdfLabels = {
  invoiceMonth: 'MÊS DA FATURA',
  contractNumber: 'NÚMERO DO CONTRATO',
  subestipulanteName: 'SUB-ESTIPULANTE',
  type: 'TIPO',
  dueDate: 'VENCIMENTO',
  status: 'STATUS',
  amountSection: 'Valor',
};

const BRADESCO_RED = { r: 204, g: 9, b: 47 };
const GRAY_LABEL = { r: 107, g: 114, b: 128 };
const GRAY_BORDER = { r: 209, g: 213, b: 219 };
const LIGHT_BG = { r: 249, g: 250, b: 251 };

let brandLogoDataUrlCache: string | null = null;

/**
 * Sanitizes a string for use in filename
 */
const sanitizeFileName = (str: string): string => {
  return str.replace(/[^a-zA-Z0-9]/g, '_');
};

/**
 * Converts hex color to RGB
 */
const hexToRgb = (hex: string): { r: number; g: number; b: number } | null => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16),
    }
    : null;
};

const loadBrandLogoDataUrl = async (): Promise<string | null> => {
  if (brandLogoDataUrlCache) {
    return brandLogoDataUrlCache;
  }

  try {
    const response = await fetch(brandLogoUrl);
    if (!response.ok) {
      return null;
    }

    const blob = await response.blob();
    brandLogoDataUrlCache = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });

    return brandLogoDataUrlCache;
  } catch {
    return null;
  }
};

const setBrandDrawColor = (doc: jsPDF): void => {
  doc.setDrawColor(BRADESCO_RED.r, BRADESCO_RED.g, BRADESCO_RED.b);
};

const drawHorizontalRule = (
  doc: jsPDF,
  x: number,
  y: number,
  width: number,
  lineWidth: number = 0.4,
): void => {
  setBrandDrawColor(doc);
  doc.setLineWidth(lineWidth);
  doc.line(x, y, x + width, y);
};

const drawSectionHeader = (
  doc: jsPDF,
  title: string,
  pageWidth: number,
  y: number,
): number => {
  const centerX = pageWidth / 2;
  const fontSize = 10;

  doc.setFontSize(fontSize);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(BRADESCO_RED.r, BRADESCO_RED.g, BRADESCO_RED.b);
  doc.text(title, centerX, y + 4, { align: 'center' });

  return y + 12;
};

const drawGridField = (
  doc: jsPDF,
  label: string,
  value: string,
  x: number,
  y: number,
  width: number,
  height: number,
  valueColor?: { r: number; g: number; b: number },
): void => {
  doc.setDrawColor(GRAY_BORDER.r, GRAY_BORDER.g, GRAY_BORDER.b);
  doc.setLineWidth(0.2);
  doc.rect(x, y, width, height);

  doc.setFontSize(7);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(GRAY_LABEL.r, GRAY_LABEL.g, GRAY_LABEL.b);
  doc.text(label.toUpperCase(), x + 3, y + 5);

  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  const color = valueColor ?? { r: 0, g: 0, b: 0 };
  doc.setTextColor(color.r, color.g, color.b);

  const valueLines = doc.splitTextToSize(value || '—', width - 6) as string[];
  doc.text(valueLines, x + 3, y + 12);
};

const addBrandDocumentHeader = async (
  doc: jsPDF,
  options: {
    title: string;
    subtitle: string;
    documentId: string;
    margin: number;
    pageWidth: number;
  },
): Promise<number> => {
  const { title, subtitle, documentId, margin, pageWidth } = options;
  let y = margin;

  const logoDataUrl = await loadBrandLogoDataUrl();
  const logoWidth = 52;
  const logoHeight = 9;

  if (logoDataUrl) {
    doc.addImage(logoDataUrl, 'PNG', margin, y, logoWidth, logoHeight);
    y += logoHeight + 4;
  }

  drawHorizontalRule(doc, margin, y, pageWidth - margin * 2, 1.2);
  y += 6;

  const headerContentY = y;
  const centerX = pageWidth / 2;
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(BRADESCO_RED.r, BRADESCO_RED.g, BRADESCO_RED.b);
  doc.text(title, centerX, headerContentY + 5, { align: 'center' });

  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(GRAY_LABEL.r, GRAY_LABEL.g, GRAY_LABEL.b);
  doc.text(subtitle, centerX, headerContentY + 10, { align: 'center' });

  const metaX = pageWidth - margin;
  const generatedDate = new Date().toLocaleDateString('pt-BR');

  doc.setFontSize(7);
  doc.setTextColor(GRAY_LABEL.r, GRAY_LABEL.g, GRAY_LABEL.b);
  doc.text('Nº DO DOCUMENTO', metaX, headerContentY + 2, { align: 'right' });

  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0, 0, 0);
  doc.text(String(documentId), metaX, headerContentY + 7, { align: 'right' });

  doc.setFontSize(7);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(GRAY_LABEL.r, GRAY_LABEL.g, GRAY_LABEL.b);
  doc.text('DATA', metaX, headerContentY + 12, { align: 'right' });

  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0, 0, 0);
  doc.text(generatedDate, metaX, headerContentY + 17, { align: 'right' });

  y = headerContentY + 22;
  drawHorizontalRule(doc, margin, y, pageWidth - margin * 2, 0.4);

  return y + 6;
};

const addBrandFooter = (doc: jsPDF, margin: number, pageWidth: number, pageHeight: number): void => {
  const footerY = pageHeight - 24;

  drawHorizontalRule(doc, margin, footerY, pageWidth - margin * 2, 0.4);

  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(BRADESCO_RED.r, BRADESCO_RED.g, BRADESCO_RED.b);
  doc.text('BRADESCO SEGUROS', margin, footerY + 4);

  doc.setFontSize(6);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(GRAY_LABEL.r, GRAY_LABEL.g, GRAY_LABEL.b);
  doc.text('CNPJ: 33.665.021/0001-04', margin, footerY + 7);
  doc.text('https://www.bradescoseguros.com.br', margin, footerY + 10);

  const disclaimer = 'Documento gerado eletronicamente para consulta de faturas.';
  doc.text(disclaimer, margin, footerY + 14, { maxWidth: pageWidth * 0.55 });

  const generatedAt = new Date().toLocaleString('pt-BR');
  doc.text(`Documento gerado em ${generatedAt}`, pageWidth - margin, footerY + 5, { align: 'right' });
  doc.text('Página 1 de 1', pageWidth - margin, footerY + 9, { align: 'right' });

  drawHorizontalRule(doc, margin, pageHeight - 8, pageWidth - margin * 2, 1.2);
};

/**
 * Gets status color for invoice value styling
 */
const getInvoiceStatusColor = (status: string): { r: number; g: number; b: number } => {
  const normalized = status.trim().toLowerCase();
  const statusColors: Record<string, string> = {
    pago: '#10b981',
    pendente: '#f59e0b',
    vencido: '#ef4444',
    cancelado: '#6b7280',
    'em andamento': '#3b82f6',
    'em dia': '#10b981',
    atenção: '#f59e0b',
    atencao: '#f59e0b',
    atrasado: '#ef4444',
  };

  const hex = statusColors[normalized] || '#374151';
  return hexToRgb(hex) ?? { r: 55, g: 65, b: 81 };
};

const drawAmountSection = (
  doc: jsPDF,
  label: string,
  amount: string,
  margin: number,
  y: number,
  contentWidth: number,
): number => {
  const boxHeight = 22;

  doc.setFillColor(LIGHT_BG.r, LIGHT_BG.g, LIGHT_BG.b);
  doc.rect(margin, y, contentWidth, boxHeight, 'F');

  doc.setFillColor(BRADESCO_RED.r, BRADESCO_RED.g, BRADESCO_RED.b);
  doc.rect(margin, y, 2.5, boxHeight, 'F');

  doc.setDrawColor(GRAY_BORDER.r, GRAY_BORDER.g, GRAY_BORDER.b);
  doc.setLineWidth(0.2);
  doc.rect(margin, y, contentWidth, boxHeight);

  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(GRAY_LABEL.r, GRAY_LABEL.g, GRAY_LABEL.b);
  doc.text(label.toUpperCase(), margin + 8, y + 8);

  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(BRADESCO_RED.r, BRADESCO_RED.g, BRADESCO_RED.b);
  doc.text(amount, margin + contentWidth - 8, y + 15, { align: 'right' });

  return y + boxHeight + 10;
};

/**
 * Generates a PDF document for an invoice and downloads it automatically
 * @param invoice - Invoice data to export
 * @param formatCurrency - Function to format currency values
 * @param formatDate - Function to format dates
 * @param formatBillingMonth - Optional function to format billing month
 */
export const generateInvoicePDF = async (
  invoice: InvoiceHistoryItem,
  formatCurrency: (value: number) => string,
  formatDate: (dateStr: string) => string,
  formatBillingMonth?: (dateStr: string) => string,
  labels: InvoicePdfLabels = DEFAULT_INVOICE_PDF_LABELS,
): Promise<void> => {
  const month = invoice.billingMonth
    ? (formatBillingMonth ? formatBillingMonth(invoice.billingMonth) : invoice.billingMonth)
    : (invoice.invoiceMonth || '');
  const policyNumber = invoice.contractNumber || invoice.policyNumber || '';

  const sanitizedMonth = sanitizeFileName(month);
  const fileName = `Fatura_${sanitizedMonth}_${policyNumber}.pdf`;

  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 16;
  const contentWidth = pageWidth - margin * 2;

  let yPosition = await addBrandDocumentHeader(doc, {
    title: 'FATURA',
    subtitle: 'Detalhamento de Pagamento - Vida Empresarial',
    documentId: String(invoice.id),
    margin,
    pageWidth,
  });

  yPosition = drawSectionHeader(doc, 'DADOS DA FATURA', pageWidth, yPosition);

  const cellHeight = 18;
  const cellGap = 0;
  const columns = 3;
  const cellWidth = (contentWidth - cellGap * (columns - 1)) / columns;

  const fields: Array<{ label: string; value: string; color?: { r: number; g: number; b: number } }> = [
    { label: labels.invoiceMonth, value: month },
    { label: labels.contractNumber, value: policyNumber },
    { label: labels.subestipulanteName, value: formatSubestipulanteCodNome(invoice.codSubestipulante, invoice.nomeSubestipulante) },
    { label: labels.type, value: invoice.type },
    { label: labels.dueDate, value: formatDate(invoice.dueDate) },
    { label: labels.status, value: invoice.status, color: getInvoiceStatusColor(invoice.status) },
  ];

  fields.forEach((field, index) => {
    const col = index % columns;
    const row = Math.floor(index / columns);
    const x = margin + col * (cellWidth + cellGap);
    const y = yPosition + row * (cellHeight + cellGap);

    drawGridField(doc, field.label, field.value, x, y, cellWidth, cellHeight, field.color);
  });

  const rowsUsed = Math.ceil(fields.length / columns);
  yPosition += rowsUsed * (cellHeight + cellGap) + 8;

  yPosition = drawSectionHeader(doc, 'VALOR', pageWidth, yPosition);
  drawAmountSection(doc, labels.amountSection, formatCurrency(invoice.amount), margin, yPosition, contentWidth);

  addBrandFooter(doc, margin, pageWidth, pageHeight);

  doc.save(fileName);
};
